// Отчёты по заявкам («форма Борисова», 3 этапа) — зеркало портала 73. ТОЛЬКО ЧТЕНИЕ.
//
// ОСОБЕННОСТЬ ЭТИХ ДАННЫХ. В отличие от верификаций, в отчёте не сохранены подписи полей —
// в зеркале лежат только коды (`deadline`, `travel_to`) и значения. Подписи и порядок полей
// берём из копии схемы формы портала (lib/p73.ts, ЭТАПЫ_ОТЧЁТА). Если портал добавит поле,
// оно покажется кодом и будет помечено — экран об этом скажет, а не промолчит.
import { useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { useRefs } from '../api/hooks'
import { useP73Refs, useОтчёт, useОтчёты } from '../api/p73'
import type { ОтчётКарточка, ОтчётСтрока, ФильтрыP73, ЭтапОтчёта } from '../api/p73'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { PhotoGallery } from '../components/PhotoGallery'
import { PrintButton } from '../components/PrintButton'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtText } from '../lib/format'
import { листОтчёта } from '../lib/print73'
import {
  ПУТИ, РАЗМЕР_СТРАНИЦЫ, видСтатуса, долгоеПоле, значениеФормы, названиеСтатуса, поляЭтапа,
  процент, размерФайла, схемаЭтапа, ЭТАПЫ_ОТЧЁТА,
} from '../lib/p73'

export default function JobReports() {
  const { id } = useParams<{ id: string }>()
  return id ? <Карточка id={id} /> : <Список />
}

// ═══════════════════════════════ список ═══════════════════════════════

function Список() {
  const [sp, setSp] = useSearchParams()
  const vesselBx = sp.get('vessel_bx') || ''
  const dealBx = sp.get('deal_bx') || ''

  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [engineer, setEngineer] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [start, setStart] = useState(0)

  const refs = useRefs()
  const p73 = useP73Refs()

  const фильтры = useMemo<ФильтрыP73>(() => ({
    q: q.trim() || undefined,
    status: status || undefined,
    engineer: engineer || undefined,
    vessel_bx: vesselBx || undefined,
    deal_bx: dealBx || undefined,
    from: from || undefined,
    to: to || undefined,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [q, status, engineer, vesselBx, dealBx, from, to, start])

  const запрос = useОтчёты(фильтры)
  const данные = запрос.data
  const сСброса = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  const снять = (ключ: string) => {
    const next = new URLSearchParams(sp)
    next.delete(ключ)
    setStart(0)
    setSp(next, { replace: true })
  }

  return (
    <>
      <div className="page-head">
        <h1>Отчёты по заявкам</h1>
        <span className="muted">
          {данные ? `найдено: ${данные.total}` : ''}
          {p73.data ? ` · всего в ядре: ${p73.data.counts.job_reports}` : ''}
        </span>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        {/* Пять полей в столбец на телефоне отодвигают первую строку списка за экран,
            поэтому блок сворачивается; применённое перечислено в его заголовке. */}
        <FilterBar id="job-reports">
          <div className="f" style={{ flex: 2, minWidth: 240 }}>
            <label htmlFor="jq">Судно, IMO или название сделки</label>
            <input id="jq" value={q} onChange={(e) => сСброса(setQ)(e.target.value)}
                   placeholder="например: Ахмат или 8954946" />
          </div>

          <div className="f">
            <label htmlFor="jst">Статус</label>
            <select id="jst" value={status} onChange={(e) => сСброса(setStatus)(e.target.value)}>
              <option value="">любой</option>
              {Object.entries(p73.data?.job_report_status ?? {}).map(([к, n]) => (
                <option key={к} value={к}>{названиеСтатуса(к)} ({n})</option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 200 }}>
            <label htmlFor="jen">Инженер</label>
            <select id="jen" value={engineer} onChange={(e) => сСброса(setEngineer)(e.target.value)}
                    disabled={!p73.data}>
              <option value="">любой</option>
              {(p73.data?.engineers ?? []).map((u) => (
                <option key={u.d1_id} value={u.d1_id}>
                  {u.full_name || u.username || `#${u.d1_id}`}{u.is_active ? '' : ' (не работает)'}
                </option>
              ))}
            </select>
          </div>

          <div className="f">
            <label htmlFor="jfrom">Изменён с</label>
            <input id="jfrom" type="date" value={from} onChange={(e) => сСброса(setFrom)(e.target.value)} />
          </div>
          <div className="f">
            <label htmlFor="jto">по</label>
            <input id="jto" type="date" value={to} onChange={(e) => сСброса(setTo)(e.target.value)} />
          </div>
        </FilterBar>

        {(vesselBx || dealBx) && (
          <div className="hint" style={{ marginTop: 10 }}>
            Отфильтровано по{' '}
            {vesselBx && (<>судну <EntityLink kind="vessel" id={vesselBx} resolve />{' '}
              <button onClick={() => снять('vessel_bx')}>снять</button>{' '}</>)}
            {dealBx && (<>сделке <EntityLink kind="deal" id={dealBx} resolve />{' '}
              <button onClick={() => снять('deal_bx')}>снять</button></>)}
          </div>
        )}
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <div className="card">
          {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">ID</th>
                  <th>Судно</th>
                  <th>Заявка / сделка</th>
                  <th className="nowrap">Инженер</th>
                  <th className="nowrap">Статус</th>
                  <th className="num">Заполнено</th>
                  <th className="num">Фото</th>
                  <th className="nowrap">Изменён</th>
                </tr>
              </thead>
              <tbody>
                {(данные?.rows ?? []).map((r) => <Строка key={r.d1_id} r={r} имяСотрудника={refs.userNameOr} />)}
                {данные && данные.rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="empty">
                      <div className="empty-title">Под эти фильтры отчётов нет</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {данные && данные.total > РАЗМЕР_СТРАНИЦЫ && (
            <div className="pager">
              <button disabled={start === 0 || запрос.isFetching}
                      onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>← назад</button>
              <span className="muted">
                {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, данные.total)} из {данные.total}
              </span>
              <button disabled={данные.next === null || запрос.isFetching}
                      onClick={() => setStart(данные.next!)}>вперёд →</button>
            </div>
          )}
        </div>
      </QueryState>
    </>
  )
}

function Строка({ r, имяСотрудника }: {
  r: ОтчётСтрока
  /** refs.userNameOr: имя из справочника ядра, а нет его — имя из учётки портала 73. */
  имяСотрудника: (id?: string | number | null, fallback?: string | null) => string
}) {
  return (
    <tr>
      <td className="mono nowrap" data-label="ID"><Link to={ПУТИ.отчёт(r.d1_id)}>{r.d1_id}</Link></td>
      <td data-label="Судно">
        <EntityLink kind="vessel" id={r.vessel?.bitrix_id} title={r.vessel?.name}
                    empty={r.vessel?.name || '— нет мостика к контакту'} />
        {r.vessel?.imo && <div className="muted mono">IMO {r.vessel.imo}</div>}
      </td>
      {/* Обрезаем: названия сделок длинные, полное видно подсказкой на ссылке */}
      <td data-label="Заявка / сделка"
          style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {r.deal?.bitrix_id
          ? <EntityLink kind="deal" id={r.deal.bitrix_id} title={r.deal.title} />
          : <span className="muted">сделки Bitrix нет</span>}
      </td>
      <td className="nowrap" data-label="Инженер">{имяСотрудника(r.engineer?.bitrix_id, r.engineer?.name)}</td>
      <td className="nowrap" data-label="Статус"><span className={`badge ${видСтатуса(r.status)}`}>{названиеСтатуса(r.status)}</span></td>
      <td className="num" data-label="Заполнено">{процент(r.fill_pct)}</td>
      {/* Ноль фотографий — факт, а не «неизвестно» (см. то же место в Verifications). */}
      <td className="num" data-label="Фото">
        {r.photos_total > 0 ? r.photos_total : <span className="muted" title="фотографий не приложено">0</span>}
      </td>
      <td className="nowrap muted" data-label="Изменён">{fmtDate(r.updated_at || r.created_at, true)}</td>
    </tr>
  )
}

// ═══════════════════════════════ карточка ═══════════════════════════════

function Карточка({ id }: { id: string }) {
  const запрос = useОтчёт(id)
  const refs = useRefs()
  const r = запрос.data

  // Порядок этапов — из схемы портала; то, чего в схеме нет, ставим в конец и помечаем.
  const этапы = useMemo(() => {
    if (!r) return []
    const по_коду = new Map(r.stages.map((э) => [э.code, э]))
    const итог: Array<{ этап: ЭтапОтчёта; вСхеме: boolean }> = []
    for (const с of ЭТАПЫ_ОТЧЁТА) {
      const э = по_коду.get(с.code)
      if (э) {
        итог.push({ этап: э, вСхеме: true })
        по_коду.delete(с.code)
      }
    }
    for (const э of по_коду.values()) итог.push({ этап: э, вСхеме: false })
    return итог
  }, [r])

  return (
    <>
      <div className="page-head">
        <h1>Отчёт по заявке #{id}</h1>
        <div className="spacer" />
        {/* Лист для человека. Подписи полей — копия схемы формы портала: в данных ядра
            лежат только коды, и поле, показанное кодом, значит «форма ушла вперёд». */}
        <PrintButton готово={!!r} собрать={(св) => листОтчёта(r!, { свежесть: св })} />
        <Link to={ПУТИ.отчёты}>← к списку</Link>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {r && (
          <>
            <div className="grid-2">
              <div className="card">
                <h2>Отчёт</h2>
                <dl className="dl">
                  <dt>Судно</dt>
                  <dd>
                    <EntityLink kind="vessel" id={r.vessel?.bitrix_id} title={r.vessel?.name}
                                empty={r.vessel?.name || '—'} />
                    {r.vessel?.imo ? <span className="muted mono"> · IMO {r.vessel.imo}</span> : null}
                  </dd>
                  <dt>Заявка / сделка</dt>
                  <dd>
                    {r.deal?.bitrix_id
                      ? <EntityLink kind="deal" id={r.deal.bitrix_id} title={r.deal.title} />
                      : <span className="muted">сделки Bitrix нет</span>}
                    {r.deal?.location ? <span className="muted"> · {r.deal.location}</span> : null}
                  </dd>
                  <dt>Инженер</dt>
                  <dd>{refs.userNameOr(r.engineer?.bitrix_id, r.engineer?.name)}</dd>
                  <dt>Статус</dt>
                  <dd>
                    <span className={`badge ${видСтатуса(r.status)}`}>{названиеСтатуса(r.status)}</span>
                    {r.reject_reason ? <div className="error" style={{ marginTop: 6 }}>Причина отклонения: {r.reject_reason}</div> : null}
                  </dd>
                  <dt>Заполнено</dt><dd>{процент(r.fill_pct)}</dd>
                  <dt>Создан</dt><dd className="muted">{fmtDate(r.created_at, true)}</dd>
                  <dt>Изменён</dt><dd className="muted">{fmtDate(r.updated_at, true)}</dd>
                  <dt>Отправлен</dt><dd className="muted">{fmtDate(r.submitted_at, true)}</dd>
                  <dt>Проверен</dt>
                  <dd className="muted">
                    {fmtDate(r.reviewed_at, true)}{r.reviewer_name ? ` · ${r.reviewer_name}` : ''}
                  </dd>
                  <dt>Фотографий</dt><dd>{r.photos_total || 0}</dd>
                </dl>
              </div>

              <div className="card">
                <h2>Что важно знать про эти данные</h2>
                <ul className="plain-list">
                  <li>
                    Подписи полей в зеркале не сохранены — в данных только коды. Названия
                    пунктов ниже взяты из копии схемы формы портала 73; если портал форму
                    менял, новое поле покажется кодом и будет помечено «нет в схеме».
                  </li>
                  <li>
                    Фотографии остаются в хранилище портала: здесь видно, к какому этапу и
                    сколько их приложено, но открыть их можно только на портале.
                  </li>
                  <li>
                    Порядок этапов и полей — из схемы, а не из данных: jsonb в Postgres не
                    хранит порядок ключей.
                  </li>
                  {r.points_total === null && (
                    <li>
                      Баллы за отчёт портал посчитал только у одной записи из 45 — в этом
                      отчёте их нет, и это не значит «ноль баллов».
                    </li>
                  )}
                  {r.meta.unknown_keys.length > 0 && (
                    <li className="error">
                      В данных есть ключи, которые разобрать не удалось: {r.meta.unknown_keys.join(', ')}.
                    </li>
                  )}
                  {Object.keys(r.meta.photos_other).length > 0 && (
                    <li className="error">
                      Фотографии привязаны к секциям, которых нет в отчёте:{' '}
                      {Object.entries(r.meta.photos_other).map(([k, n]) => `${k} (${n})`).join(', ')}.
                      Их не потеряли — просто показать их не в чем.
                    </li>
                  )}
                </ul>
              </div>
            </div>

            {этапы.length === 0 && (
              <div className="card muted">Этапы в записи пусты — на портале форму не заполняли.</div>
            )}
            {этапы.map(({ этап, вСхеме }) => (
              <Этап key={этап.code} этап={этап} вСхеме={вСхеме} />
            ))}

            <Фото r={r} />
          </>
        )}
      </QueryState>
    </>
  )
}

/** Сколько фотографий приложено к блоку. Ноль не пишем — пустая приписка только мешает. */
const плашкаФото = (n: number) =>
  n ? <span className="hint" style={{ fontWeight: 'normal' }}> · фото: {n}</span> : null

function Этап({ этап, вСхеме }: { этап: ЭтапОтчёта; вСхеме: boolean }) {
  const схема = схемаЭтапа(этап.code)
  const заголовок = схема?.title || этап.title || этап.code
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>{заголовок}</h2>
        <div className="spacer" />
        <span className="muted">
          {!вСхеме && <span className="badge lose" style={{ marginRight: 8 }}>нет в схеме формы</span>}
          {этап.photos ? `фото: ${этап.photos}` : ''}
        </span>
      </div>

      {этап.repeat ? (
        (этап.blocks ?? []).length === 0
          ? <div className="empty"><div className="empty-title">Блоков нет.</div></div>
          : (этап.blocks ?? []).map((б, i) => (
            <div key={i} style={{ marginBottom: 14 }}>
              <h3 style={{ marginBottom: 6 }}>
                {б.label}{' '}
                {б.seeded && (
                  <span className="hint" style={{ fontWeight: 'normal' }}>
                    — блок создан предзаполнением формы
                  </span>
                )}
                {плашкаФото(б.photos)}
              </h3>
              <Поля code={этап.code} items={б.items} />
            </div>
          ))
      ) : (
        <Поля code={этап.code} items={этап.items} />
      )}
    </div>
  )
}

function Поля({ code, items }: { code: string; items?: Array<{ key: string; value: unknown }> }) {
  const поля = поляЭтапа(code, items)
  if (!поля.length) return <div className="empty"><div className="empty-title">Полей нет.</div></div>
  return (
    <dl className="dl">
      {поля.map((п) => (
        <Пара key={п.key} п={п} долгое={долгоеПоле(code, п.key) || п.long} />
      ))}
    </dl>
  )
}

function Пара({ п, долгое }: {
  п: { key: string; label: string; value: unknown; unknown: boolean }
  долгое: boolean
}) {
  const пусто = !п.value && п.value !== 0
  return (
    <>
      <dt>
        {п.label}
        {п.unknown && <div className="muted mono">нет в схеме формы</div>}
      </dt>
      <dd style={долгое ? { whiteSpace: 'pre-wrap' } : undefined}>
        {пусто ? <span className="muted">—</span> : значениеФормы(п.value)}
      </dd>
    </>
  )
}

function Фото({ r }: { r: ОтчётКарточка }) {
  if (!r.photos.length) {
    return <div className="card muted">Фотографий к отчёту в ядре нет.</div>
  }
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Фотографии</h2>
        <div className="spacer" />
        <span className="muted">{r.photos_total}</span>
      </div>

      {/* Для отчёта по заявке снимок — это и есть содержание: чем закончилась работа на
          судне, из кодов полей не видно. Поэтому галерея стоит ПЕРЕД реестром.
          Пустой список сюда не доходит — он отсечён выше, вместе со всей карточкой. */}
      <PhotoGallery вид="job-report-photo" файлы={r.photos} />

      <h3 style={{ marginTop: 20 }}>Реестр файлов</h3>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th className="nowrap">Секция</th>
              <th>Файл</th>
              <th className="num">Размер</th>
              <th className="nowrap">Загружен</th>
            </tr>
          </thead>
          <tbody>
            {r.photos.map((ф) => (
              <tr key={ф.d1_id}>
                <td className="mono nowrap" data-label="Секция">{fmtText(ф.section_key)}</td>
                <td data-label="Файл">
                  {fmtText(ф.label)}
                  <div className="muted mono">{fmtText(ф.r2_key)}</div>
                </td>
                <td className="num muted" data-label="Размер">{размерФайла(ф.size_bytes)}</td>
                <td className="nowrap muted" data-label="Загружен">{fmtDate(ф.created_at, true)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="hint" style={{ marginTop: 8 }}>
        В зеркале только ключи: файлы остаются в хранилище портала 73, ядро читает их своим
        ключом «только чтение». Секция вида «stage3_days_0» — первый день третьего этапа.
      </div>
    </div>
  )
}
